/* eslint-disable max-lines */
import { observable, action, computed } from 'mobx';
import BLChatLibrary from '@tempted1/blacklabel-chat-saas-lib';

import ApiService from 'services/ApiService/ApiService';
import { API_STATES } from 'pages/Settings/Inbox-new/State/utils';
import logger from 'logger';

import coreService from './core';


const chatLogger = logger.child({ childName: 'fanClubService/chatStore' });

const sortByTimestamp = (a, b) => {
  const timestampA = a.lastConversationMessage?.timestamp;
  const timestampB = b.lastConversationMessage?.timestamp;

  if (timestampA < timestampB) {
    return 1;
  }
  if (timestampA > timestampB) {
    return -1;
  }

  return 0;
};

const sortMarkedConversations = (a, b) => {
  if (a.conversationMarked < b.conversationMarked) {
    return 1;
  }
  if (a.conversationMarked > b.conversationMarked) {
    return -1;
  }

  return sortByTimestamp(a, b);
};

const sortBySharedStatus = (a, b) => {
  const aIsShared = a.sharedWithFan;
  const bIsShared = b.sharedWithFan;

  if (aIsShared > bIsShared) {
    return 1;
  }
  if (aIsShared < bIsShared) {
    return -1;
  }

  return 0;
};

class ChatStore {
  blcInstance = null;
  activeSession = null;
  isFirstGlobalEvent = true;

  @observable _channels = {};
  @observable _friends = [];
  @observable _fans = [];
  @observable _fansPlus = [];
  @observable _subscriptions = [];
  @observable _allFans = [];

  @observable loading = false;
  @observable error = false;
  @observable user = {
    id: undefined,
    isPerformer: false,
  };

  @observable activeConversation = {
    canLoadMore: false,
    details: {},
    loadingState: API_STATES.INITIAL,
    messages: [],
    allSharedContent: {
      loadingState: API_STATES.INITIAL,
      galleries: [],
      videos: [],
    },
  };

  @observable previewImages = new Map();

  @observable deleteMessageLoadingState = API_STATES.INITIAL;
  @observable editMessageLoadingState = API_STATES.INITIAL;

  @observable mutedUserIds = [];
  @observable muteUserLoadingState = API_STATES.INITIAL;

  @observable isStartConversationModalOpen = false;
  @observable startConversationLoadingState = API_STATES.INITIAL;

  @observable isShareContentModalOpen = false;
  @observable getAllShareableContentLoadingState = API_STATES.INITIAL;
  @observable allShareableContent = {
    galleries: null,
    videos: null,
  };

  @observable shareContentLoadingState = API_STATES.INITIAL;

  @observable isShareLiveShowModalOpen = false;
  @observable shareLiveShowModalDetails = {};
  @observable shareLiveShowLoadingState = API_STATES.INITIAL;

  @observable newMessageContactsLoadingState = API_STATES.INITIAL;

  @observable lightboxDetails = {
    loading: false,
    error: false,
    type: undefined,
    content: [],
  };

  @computed get activeSubscriptions() {
    const results = [];

    for (const name of this._subscriptions) {
      const item = this._channels[name];
      if (item.lastConversationMessage && !item.disabled) {
        results.push(Object.assign({}, item));
      }
    }

    return results;
  }

  @computed get inactiveSubscriptions() {
    const results = [];

    for (const name of this._subscriptions) {
      const item = this._channels[name];
      if (item.lastConversationMessage && item.disabled) {
        results.push(Object.assign({}, item));
      }
    }

    return results;
  }

  @computed get friends() {
    const result = [];

    for (const name of this._friends) {
      const item = this._channels[name];
      if (item?.lastConversationMessage) {
        result.push(Object.assign({}, item));
      }
    }

    return result.sort(sortByTimestamp);
  }

  filterSilencedUsers = ({ peerUserId }) => !this.mutedUserIds.includes(peerUserId);

  @computed get fans() {
    return this._fans
      .map((name) => Object.assign({}, this._channels[name]))
      .filter(this.filterSilencedUsers)
      .sort(sortMarkedConversations);
  }

  @computed get fansPlus() {
    return this._fansPlus
      .map((name) => Object.assign({}, this._channels[name]))
      .filter(this.filterSilencedUsers)
      .sort(sortMarkedConversations);
  }

  @computed get newMessageContacts() {
    const result = [];

    if (this.newMessageContactsLoadingState === API_STATES.LOADING) {
      return result;
    }

    for (const id of this._friends) {
      const {
        lastConversationMessage,
        name,
        peerDisplayName,
        peerProfileImage,
      } = this._channels[id];

      if (!lastConversationMessage) {
        result.push({
          chatSilenced: false,
          conversationName: name,
          fanDisplayName: peerDisplayName,
          peerProfileImage,
        });
      }
    }

    const activeFansMap = {};

    for (const id of this._fans) {
      const item = this._channels[id];
      activeFansMap[item.peerDisplayName] = true;
    }

    for (const id of this._fansPlus) {
      const item = this._channels[id];
      activeFansMap[item.peerDisplayName] = true;
    }

    for (const fan of this._allFans) {
      const isFanDisplayed = Boolean(activeFansMap[fan.fanDisplayName]);
      if (fan.chatSilenced || !isFanDisplayed) {
        result.push(fan);
      }
    }

    return result;
  }

  @computed get newMessageFanContacts() {
    const results = [];

    for (const name of this._subscriptions) {
      const item = this._channels[name];
      if (item && !item.lastConversationMessage) {
        const {
          name: conversationName,
          peerDisplayName,
          peerProfileImage,
        } = item;

        results.push({
          chatSilenced: false,
          conversationName,
          fanDisplayName: peerDisplayName,
          peerProfileImage,
        });
      }
    }

    return results;
  }

  @action init = async() => {
    this.setLoadingStart();

    const { id, isPerformer } = await coreService.getUser();
    this.setUserDetails({ id, isPerformer });

    try {
      await this.fetchConversationLists();
    } catch (error) {
      this.setLoadingFinish(error);
      chatLogger.error({ msg: 'Could not fetch conversations', error });

      return;
    }

    try {
      this.blcInstance = new BLChatLibrary();

      const { token } = await coreService.getToken();
      await this.blcInstance.init(token, coreService.baseUrl);
    } catch (error) {
      this.blcInstance = null;
      this.setLoadingFinish(error);
      chatLogger.error({ msg: 'Could not initialize BLChatLibrary', error });

      return;
    }

    this.setLoadingFinish();

    this.isFirstGlobalEvent = true;

    try {
      this.blcInstance.globalEventsSubscribe(this.onGlobalEvent);
    } catch (err) {
      // just safeguarding
    }
  };

  @action loadConversation = async(conversation = {}) => {
    this.closeConversation();

    this.activeConversation.loadingState = API_STATES.LOADING;
    this.activeConversation.details = observable({ ...conversation });
    this.activeConversation.messages = [];

    const { token } = await coreService.getToken();
    const { name } = conversation;

    this.blcInstance.createConversationSession(token, name, this.onConversationLoad);
  };

  @action closeConversation = () => {
    this.activeSession?.leaveConversation?.();
    this.activeSession = null;

    this.activeConversation = observable({
      canLoadMore: false,
      details: {},
      loadingState: API_STATES.INITIAL,
      messages: [],
      allSharedContent: {
        loadingState: API_STATES.INITIAL,
        galleries: [],
        videos: [],
      },
    });
  };

  @action openStartConversationModal = () =>
    this.isStartConversationModalOpen = true;

  @action closeStartConversationModal = () =>
    this.isStartConversationModalOpen = false;

  @action openShareContentModal = () =>
    this.isShareContentModalOpen = true;

  @action closeShareContentModal = () => {
    this.isShareContentModalOpen = false;
    this.shareContentLoadingState = API_STATES.INITIAL;
  };

  @action openLiveShowRequestModal = ({ liveShowId, liveShowTimestamp }) => {
    this.isShareLiveShowModalOpen = true;
    this.shareLiveShowModalDetails = { liveShowId, liveShowTimestamp };
  };

  @action closeLiveShowRequestModal = () => {
    this.isShareLiveShowModalOpen = false;
    this.shareLiveShowModalDetails = {};
    this.shareLiveShowLoadingState = API_STATES.INITIAL;
  };

  @action markAsFavorite = async(messageName, value) => {
    const { token } = await coreService.getToken();
    this.blcInstance.updateMarkedValue(token, messageName, value, this.onMessageMarked(messageName, value));
  };

  @action getResourceDetails = (messageId, resourceId) => {
    if (!messageId || !resourceId || !this.activeSession) {
      chatLogger.error('[getResourceDetails] The payload or the activeSession is undefined');

      return;
    }

    this.activeSession.getResourceDetails(resourceId, this.onResourceDetailsLoaded(messageId));
  };

  @action getMessages = (options) => {
    if (!this.activeSession) {
      return;
    }

    this.activeConversation.loadingState = API_STATES.LOADING;

    const { size = 100 } = options || {};
    const { messages } = this.activeConversation;

    const [lastMessage] = messages.slice(-1);
    let { timestamp } = lastMessage || {};

    if (!timestamp) {
      timestamp = Date.now();
    } else {
      timestamp -= 1;
    }

    this.activeSession.getAroundMessages(timestamp, size, 0, this.onMessagesLoaded(size));
  };

  @action sendBroadcastMessage = async(message) => {
    if (!message || !this.blcInstance) {
      chatLogger.error('[sendBroadcastMessage] The payload or the blcInstance is undefined');

      return;
    }

    const { token } = await coreService.getToken();
    this.blcInstance.writeBroadcastMessage(message, token);
  };

  @action sendMessage = (message) => {
    if (!message || !this.activeSession) {
      chatLogger.error('[sendMessage] The payload or the activeSession is undefined');

      return;
    }

    this.activeSession.writeMessage(message);
  };

  @action deleteMessage = (messageId, isSharedContent) => {
    if (!messageId || !this.activeSession) {
      chatLogger.error('[deleteMessage] The payload or the activeSession is undefined');

      return;
    }

    this.deleteMessageLoadingState = API_STATES.LOADING;

    if (isSharedContent) {
      this.activeSession.deleteSharedContent(messageId, this.onMessageDeleted(messageId));

      return;
    }

    this.activeSession.deleteMessage(messageId, this.onMessageDeleted(messageId));
  };

  @action resetDeleteMessageState = () => this.deleteMessageLoadingState = API_STATES.INITIAL;

  @action editMessage = (messageId, newMessage) => {
    if (!messageId || !this.activeSession) {
      chatLogger.error('[editMessage] The payload or the activeSession is undefined');

      return;
    }

    this.editMessageLoadingState = API_STATES.LOADING;

    this.activeSession.editMessage(messageId, newMessage, null, this.onMessageEdited(messageId, newMessage));
  };

  @action resetEditMessageState = () => this.editMessageLoadingState = API_STATES.INITIAL;

  @action destroy = () => {
    try {
      this.closeConversation();
      this.blcInstance?.globalEventsDetach?.();
    } catch (err) {
      // just safeguarding
    }

    this.blcInstance = null;

    this._channels = observable({});
    this._friends = observable([]);
    this._fans = observable([]);
    this._fansPlus = observable([]);
    this._subscriptions = observable([]);
    this.loading = false;
    this.error = false;

    this.activeConversation = observable({
      canLoadMore: false,
      details: {},
      loadingState: API_STATES.INITIAL,
      messages: [],
      allSharedContent: {
        loadingState: API_STATES.INITIAL,
        galleries: [],
        videos: [],
      },
    });
    this.previewImages = new Map();
  };

  onGlobalEvent = (event) => {
    if (this.isFirstGlobalEvent) {
      this.isFirstGlobalEvent = false;

      return;
    }

    const { conversationName, ...lastMessage } = event;
    this.updateChannelStatus(conversationName, { lastMessage });
  };

  onConversationLoad = (session) => {
    if (!session) {
      this.activeConversation.loadingState = API_STATES.ERROR;
      this.activeConversation.messages = [];

      return;
    }

    this.activeSession = session;

    this.getMessages();
    this.activeSession.setConversationListener(this.onMessageReceived);
  };

  onMessagesLoaded = (size) => (error, messages) => {
    if (error) {
      this.activeConversation.loadingState = API_STATES.ERROR;
      chatLogger.error({ msg: 'Could not load messages', error });

      return;
    }

    const { messages: oldMessages, details } = this.activeConversation;
    if (!oldMessages.length) {
      this.activeSession.updateLastReadTimestamp(Date.now());

      const { name } = details;
      if (this._channels[name]) {
        this._channels[name].unread = false;
      }
    }

    messages.forEach(this.getSharedContentMessagePreview);

    this.activeConversation = observable({
      ...this.activeConversation,
      loadingState: API_STATES.INITIAL,
      messages: [...oldMessages, ...messages.reverse()],
      canLoadMore: messages.length === size,
    });
  };

  getSharedContentMessagePreview = (msg) => {
    const { id: userId } = this.user;
    const { sender, sharedContent, type: messageType } = msg;

    if (messageType !== 'CONTENT_SHARED') {
      return;
    }

    const {
      coverImage,
      deadline,
      resourceId,
      type,
    } = sharedContent || {};

    const isExistingResource = this.previewImages.has(resourceId);
    if (isExistingResource) {
      return;
    }

    const isExpired = (deadline - Date.now()) < 0;
    if (isExpired && sender !== 'YOU') {
      return;
    }

    if (type === 'VIDEO') {
      this.previewImages.set(resourceId, coverImage);

      return;
    }

    let modelId = userId;
    if (sender !== 'YOU') {
      modelId = this.activeConversation.details.peerModelId;
    }

    this.fetchSharedContentPreview(modelId, resourceId);
  };

  fetchSharedContentPreview = async(modelId, resourceId) => {
    const existingResource = Boolean(this.previewImages.get(resourceId));
    if (existingResource) {
      return;
    }

    const endPoint = `privateSharedContent/${modelId}/galleries/${resourceId}/thumbnail/signedUrl`;
    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const { data } = await ApiService.get(url.toString(), { headers });

      this.previewImages.set(resourceId, data.thumbnailUrl);
    } catch (error) {
      this.previewImages.set(resourceId, 'error');
    }
  };

  onMessageReceived = (event) => {
    const { type, id, sender } = event;

    if (type === 'DELETE_MESSAGE') {
      if (sender === 'PEER') {
        const isLastMessage = this.activeConversation.messages[0].id === id;

        this.activeConversation.messages = this.activeConversation.messages.filter(
          (message) => message.id !== id
        );

        if (isLastMessage) {
          const channel = this._channels[this.activeConversation.details.name];
          const lastMessage = this.activeConversation.messages[0];

          channel.lastConversationMessage = observable({
            ...channel.lastConversationMessage,
            ...lastMessage,
          });
        }
      }

      return;
    }

    if (type === 'UPDATE_MESSAGE') {
      this.updateMessage(event.content);

      return;
    }

    if (type === 'CONTENT_SHARED') {
      this.getSharedContentMessagePreview(event);
    }

    if (type !== 'LAST_READ_UPDATE') {
      this.activeConversation.messages = [event, ...this.activeConversation.messages];
    }
  };

  onMessageDeleted = (messageId) => (error) => {
    if (error) {
      this.deleteMessageLoadingState = API_STATES.ERROR;
      chatLogger.error({ msg: 'Could not delete message', error });

      return;
    }

    this.deleteMessageLoadingState = API_STATES.INITIAL;

    const isLastMessage = this.activeConversation.messages[0].id === messageId;

    this.activeConversation.messages = this.activeConversation.messages.filter(
      (message) => message.id !== messageId
    );

    if (isLastMessage) {
      const channel = this._channels[this.activeConversation.details.name];
      const lastMessage = this.activeConversation.messages[0];

      channel.lastConversationMessage = observable({
        ...channel.lastConversationMessage,
        ...lastMessage,
      });
    }
  };

  onMessageEdited = (messageId, newMessage) => (error) => {
    if (error) {
      this.editMessageLoadingState = API_STATES.ERROR;
      chatLogger.error({ msg: 'Could not edit the message', error });

      return;
    }

    this.editMessageLoadingState = API_STATES.INITIAL;
    this.updateMessage({ id: messageId, message: newMessage });
  };

  updateMessage = (event) => {
    const { id, message } = event;
    const { messages, details: activeConversationDetails } = this.activeConversation;

    const messageIndex = messages.findIndex((item) => item.id === id);
    if (messageIndex === -1) {
      return;
    }

    messages[messageIndex] = observable({
      ...messages[messageIndex],
      id,
      message,
    });

    if (messageIndex === 0) {
      const channel = this._channels[activeConversationDetails?.name];
      const lastMessage = messages[0];

      channel.lastConversationMessage = observable({
        ...channel.lastConversationMessage,
        ...lastMessage,
      });
    }
  };

  onMessageMarked = (messageId, value) => (error) => {
    if (error) {
      chatLogger.error({ msg: 'Could not update favorite status', error });

      return;
    }

    this._channels[messageId].conversationMarked = value;
  };

  onResourceDetailsLoaded = (messageId) => (_, resp) => {
    if (!resp) {
      return;
    }

    this.activeConversation.messages = this.activeConversation.messages.map(
      (message) => {
        if (message.id === messageId) {
          return { ...message, resourceDetails: resp };
        }

        return message;
      }
    );
  };

  setLoadingStart = () => {
    this.loading = true;
    this.error = false;
  };

  setLoadingFinish = (error) => {
    this.loading = false;
    this.error = Boolean(error);
  };

  setUserDetails(data = {}) {
    const { id, isPerformer } = data;

    this.user.id = id;
    this.user.isPerformer = Boolean(isPerformer);
  }

  updateChannelStatus = (name, data) => {
    const channel = this._channels[name];
    if (!channel || !data) {
      return;
    }

    const { lastMessage } = data;
    if (!lastMessage) {
      return;
    }

    const { type } = lastMessage;
    if (type === 'BROADCAST') {
      return;
    }

    const { lastConversationMessage = {} } = channel;

    const { timestamp: oldMessageTimestamp = 0 } = lastConversationMessage || {};
    const { timestamp: newMessageTimestamp } = lastMessage;
    const isOldEvent = oldMessageTimestamp > newMessageTimestamp;

    if (isOldEvent) {
      return;
    }

    const isSameMessage = oldMessageTimestamp === newMessageTimestamp;
    if (!isSameMessage) {
      channel.unread = lastMessage.sender !== 'YOU';
    }

    if (name === this.activeConversation.details.name) {
      channel.unread = false;
    }

    channel.lastConversationMessage = observable({
      ...lastConversationMessage,
      ...lastMessage,
    });
  };

  setChannelsInList = (list, newChannels = []) => {
    if (!newChannels?.length) {
      return;
    }

    newChannels.forEach((item) => {
      const { name: conversationName } = item || {};
      if (!conversationName) {
        return;
      }

      this._channels[conversationName] = observable(item);
      list.push(conversationName);
    });
  };

  setConversationLists(data) {
    if (!this.user.isPerformer) {
      this._subscriptions = [];
      this.setChannelsInList(this._subscriptions, data);

      return;
    }

    this._friends = [];
    this.setChannelsInList(this._friends, data?.friendsConversations);

    this._fans = [];
    this.setChannelsInList(this._fans, data?.fanConversations);

    this._fansPlus = [];
    this.setChannelsInList(this._fansPlus, data?.fanPlusConversations);
  }

  fetchConversationLists = async() => {
    const performerEndPoint = 'privateChat/modelConversations';
    const fanEndPoint = 'privateChat/fanConversations';

    const endPoint = this.user.isPerformer ? performerEndPoint : fanEndPoint;
    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const { data } = await ApiService.get(url.toString(), { headers });
      this.setConversationLists(data);
    } catch (error) {
      throw error;
    }
  };

  @action muteUser = async(userId) => {
    this.muteUserLoadingState = API_STATES.LOADING;
    const endPoint = 'privateChat/fansMuted';

    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      await ApiService.post(url.toString(), { headers, data: { userId } });

      this.muteUserLoadingState = API_STATES.INITIAL;
      this.mutedUserIds.push(userId);
      this.closeConversation();
    } catch (error) {
      this.muteUserLoadingState = API_STATES.ERROR;
    }
  };

  @action resetMuteUserLoadingState = () => this.muteUserLoadingState = API_STATES.INITIAL;

  @action getAllFans = async(userId) => {
    const endPoint = 'privateChat/fansWithChatStatus';

    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    this.newMessageContactsLoadingState = API_STATES.LOADING;

    try {
      const { data = [] } = await ApiService.get(url.toString(), { headers, data: { userId } });

      this._allFans = data?.filter((item) => Boolean(item?.fanDisplayName));
      this.newMessageContactsLoadingState = API_STATES.INITIAL;
    } catch (error) {
      this.newMessageContactsLoadingState = API_STATES.ERROR;
    }
  };

  @action fetchSharedContentDetails = async({ modelId, resourceId, type }) => {
    let endPoint = undefined;
    if (type === 'GALLERY') {
      endPoint = `privateSharedContent/${modelId}/galleries/${resourceId}`;
    } else if (type === 'VIDEO') {
      endPoint = `privateSharedContent/${modelId}/videos/${resourceId}`;
    }

    if (!endPoint || !resourceId) {
      return;
    }

    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    this.lightboxDetails = {
      type,
      loading: true,
      error: false,
      content: [],
    };

    try {
      const { data } = await ApiService.get(url.toString(), { headers });
      let content = [];
      if (type === 'GALLERY') {
        content = data?.images;
      } else if (type === 'VIDEO') {
        content = [data?.temporaryAccessUrl];
      }

      this.lightboxDetails = {
        type,
        loading: false,
        error: false,
        content,
      };
    } catch (error) {
      this.lightboxDetails = {
        type,
        loading: false,
        error: true,
        content: [],
      };
    }
  };

  @action closeSharedContent = () => this.lightboxDetails = {
    type: undefined,
    loading: false,
    error: false,
    content: [],
  };

  @action fetchConversationAllSharedContent = async(modelId) => {
    const endPoint = `privateSharedContent/${modelId}`;
    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    this.activeConversation.allSharedContent = {
      loadingState: API_STATES.LOADING,
      galleries: [],
      videos: [],
    };

    try {
      const { data } = await ApiService.get(url.toString(), { headers });
      const newGalleries = data.galleries ? [...data.galleries] : [];
      const newVideos = data.videos ? [...data.videos] : [];

      this.activeConversation.allSharedContent = {
        loadingState: API_STATES.INITIAL,
        galleries: newGalleries,
        videos: newVideos,
      };

      newGalleries.forEach(({ id: resourceId }) => {
        this.fetchSharedContentPreview(modelId, resourceId);
      });

      newVideos.forEach((item) => {
        const { snapshotUrl, videoId: resourceId } = item;
        this.previewImages.set(resourceId, snapshotUrl);
      });

    } catch (error) {
      this.activeConversation.allSharedContent = {
        loadingState: API_STATES.ERROR,
        galleries: [],
        videos: [],
      };
    }
  };

  @action startConversation = async(details) => {
    const { conversationName } = details;

    this.startConversationLoadingState = API_STATES.LOADING;

    if (conversationName) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      this.startConversationLoadingState = API_STATES.INITIAL;

      const channelDetails = this._channels[conversationName] || { name: conversationName };
      await this.loadConversation(channelDetails);

      if (this.user.isPerformer) {
        const friendsList = this._friends.filter((name) => name !== conversationName);
        friendsList.unshift(conversationName);
        this._friends = friendsList;
      } else {
        const subscriptions = this._subscriptions.filter((name) => name !== conversationName);
        subscriptions.unshift(conversationName);
        this._subscriptions = subscriptions;
      }

      channelDetails.lastConversationMessage = observable({ type: 'EMPTY_CONVERSATION_CREATED' });

      return;
    }

    let conversation = null;

    try {
      conversation = await this.fetchNewFanConversation(details);

      if (details.chatSilenced) {
        await this.unmuteUser(conversation.peerUserId);
      }

      this.startConversationLoadingState = API_STATES.INITIAL;
    } catch (error) {
      this.startConversationLoadingState = API_STATES.ERROR;
    }

    if (!conversation) {
      return;
    }

    this.addNewConversationToList(conversation);
    await this.loadConversation(conversation);
  };

  addNewConversationToList = (conversation) => {
    const { name, peerRole, peerFanPlus } = conversation;
    this._channels[name] = observable(conversation);

    if (!this.user.isPerformer && !this._subscriptions.includes(name)) {
      this._subscriptions.unshift(name);

      return;
    }

    if (peerRole === 'MODEL' && !this._friends.includes(name)) {
      this._friends.unshift(name);

      return;
    }

    if (peerFanPlus && !this._fansPlus.includes(name)) {
      this._fansPlus.unshift(name);

      return;
    }

    if (!this._fans.includes(name)) {
      this._fans.unshift(name);
    }
  };

  fetchNewFanConversation = async(fanDetails) => {
    const { fanConversationSessionIdentifier: id } = fanDetails;
    let endPoint = undefined;

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      endPoint = 'privateChat/modelConversations';
      const url = coreService.computeURL(endPoint);
      const payload = { fanConversationSessionIdentifier: id };

      await ApiService.post(url.toString(), { headers, data: payload });
    } catch (error) {
      // Nothing to do here. We just care that this gets called.
    }

    endPoint = `privateChat/conversationsByPartnerSessionIdentifier/${id}`;
    const url = coreService.computeURL(endPoint);

    const { data } = await ApiService.get(url.toString(), { headers });

    return data;
  };

  unmuteUser = async(userId) => {
    const endPoint = `privateChat/fansMuted/${userId}`;
    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    await ApiService.delete(url.toString(), { headers });

    this.mutedUserIds = this.mutedUserIds.filter((id) => id !== userId);
  };

  @action resetStartConversationLoadingState = () => this.startConversationLoadingState = API_STATES.INITIAL;

  @action getAllShareableContent = async({ modelId, peerConversationSessionIdentifier }) => {
    if (!peerConversationSessionIdentifier) {
      return;
    }

    const galleriesEndPoint =
      `privateContentToBeSharedByPeerConversationSessionIdentifier/${peerConversationSessionIdentifier}/galleries`;
    const videosEndPoint =
      `privateContentToBeSharedByPeerConversationSessionIdentifier/${peerConversationSessionIdentifier}/videos`;

    const galleriesUrl = coreService.computeURL(galleriesEndPoint);
    const videosUrl = coreService.computeURL(videosEndPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    const result = {
      galleries: null,
      videos: null,
    };

    this.getAllShareableContentLoadingState = API_STATES.LOADING;

    try {
      const { data } = await ApiService.get(galleriesUrl.toString(), { headers });

      result.galleries = data.content?.sort(sortBySharedStatus);
    } catch (error) {
      this.getAllShareableContentLoadingState = API_STATES.ERROR;

      return;
    }

    try {
      const { data } = await ApiService.get(videosUrl.toString(), { headers });

      result.videos = data.content?.sort(sortBySharedStatus);
    } catch (error) {
      this.getAllShareableContentLoadingState = API_STATES.ERROR;

      return;
    }

    result?.galleries?.forEach(({ id: resourceId }) => {
      this.fetchSharedContentPreview(modelId, resourceId);
    });

    result?.videos?.forEach(({ snapshotUrl, videoId: resourceId }) => {
      this.previewImages.set(resourceId, snapshotUrl);
    });

    this.getAllShareableContentLoadingState = API_STATES.INITIAL;
    this.allShareableContent = observable(result);
  };

  @action shareContent = async({ type, contentId, numberOfDays, peerConversationSessionIdentifier: urlId }) => {
    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    let endPoint = undefined;
    if (type === 'VIDEO') {
      endPoint = `privateSharedContentWithPeerConversationSessionIdentifier/${urlId}/videos`;
    } else if (type === 'GALLERY') {
      endPoint = `privateSharedContentWithPeerConversationSessionIdentifier/${urlId}/galleries`;
    }

    const url = coreService.computeURL(endPoint);
    const payload = { contentId, numberOfDays };

    this.shareContentLoadingState = API_STATES.LOADING;
    try {
      await ApiService.post(url.toString(), { headers, data: payload });

      this.shareContentLoadingState = API_STATES.INITIAL;
    } catch (error) {
      this.shareContentLoadingState = API_STATES.ERROR;
    }
  };

  @action shareLiveShow = async({ contentId, numberOfDays, peerConversationSessionIdentifier: urlId }) => {
    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    const endPoint = `privateSharedContentWithPeerConversationSessionIdentifier/${urlId}/videos`;
    const url = coreService.computeURL(endPoint);

    this.shareLiveShowLoadingState = API_STATES.LOADING;
    try {
      const payload = { contentId, numberOfDays };

      await ApiService.post(url.toString(), { headers, data: payload });

      this.shareLiveShowLoadingState = API_STATES.INITIAL;
    } catch (error) {
      this.shareLiveShowLoadingState = API_STATES.ERROR;
    }
  };

  sendAudioMessage = ({ file, callback }) => {
    this.activeSession.createResourceEntry(file, 'wav', (error, result) => {
      if (error || result.status !== 'UPLOADED') {
        chatLogger.error({ msg: 'Could not upload audio file', error });
        callback('UPLOAD_ERROR');

        return;
      }

      this.activeSession.writeResourceMessage(result.resource, undefined, (errorWriteMessage) => {
        if (errorWriteMessage) {
          chatLogger.error({ msg: 'Could not send audio message', errorWriteMessage });
          callback('SEND_MESSAGE_ERROR');

          return;
        }

        callback('INACTIVE');
      });
    });
  };
}

export default new ChatStore();
